import React from 'react';
import { useIntl } from 'react-intl';
import { isPositiveNumber } from '../../utils/numberUtils';
import { buildSalaryContextAnalysis } from '../../../../../../utils/insights/buildSalaryContextAnalysis';
import { selectPrimarySalaryBenchmark, type AllSalaryStatsLike, type SalaryStatisticsLike } from './selectPrimarySalaryBenchmark';
import { useFormatMessage } from '../../../../../../i18n-review/client/Msg';
import type { JobSalaryListingProvenance } from '../../../../../../utils/job-detail/resolve-job-salary-listing-provenance';

export interface EditorialSalaryGaugeProps {
  salaryStats?: SalaryStatisticsLike;
  locationSalaryStats?: Record<string, SalaryStatisticsLike>;
  allSalaryStats?: AllSalaryStatsLike;
  jobSalaryMin?: number;
  jobSalaryMax?: number;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  jobLocation?: string[];
  jobTitle?: string;
  jobRoleLevel?: string;
  jobSalaryListingProvenance?: JobSalaryListingProvenance;
  /** Company's real brand colour (adjustedBrand.panel); used for the headline stat, the typical-range band, and the "this offer" marker instead of the generic jc-accent green, so the gauge reads as this company's page rather than a stock chart. Falls back to jc-accent when not provided. */
  brandColor?: string;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function hexToRgba(hex: string, alpha: number): string | null {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : null;
}

function normalizeToMonthly(value: number, period: string): number {
  if (!isPositiveNumber(value)) return 0;
  switch (period) {
    case 'annual':
      return value / 12;
    case 'daily':
      return value * 22;
    case 'hourly':
      return value * 22 * 8;
    default:
      return value;
  }
}

function formatCurrency(value: number, locale: string): string {
  if (!Number.isFinite(value) || value <= 0) return '€0';
  return `€${Math.round(value).toLocaleString(locale)}`;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * TEC-512 editorial-rail redesign: a single clean gauge (typical range + market median + this
 * offer's own position) replacing the full `SalaryDistributionChart`'s five-or-six stacked
 * benchmark rows for this variant, matching the mockup's "Top 25%…" single-stat + one-gauge
 * design intent. Deliberately doesn't touch or replace `SalaryDistributionChart` itself; that
 * component is reused as-is by the control layout, and stays the source of truth for the full
 * multi-benchmark breakdown a visitor can still get to (see EditorialDossier's "View full salary
 * breakdown"; none yet; out of scope for this pass) so this is additive, not a replacement.
 */
export function EditorialSalaryGauge({
  salaryStats,
  locationSalaryStats,
  allSalaryStats,
  jobSalaryMin,
  jobSalaryMax,
  // A missing salaryPeriod means monthly (matches convertSalaryToMonthlyEUR and the processor
  // pipeline's own sanitizer default), not annual — see SalaryDistributionChart.tsx. Estimate-only
  // jobs (applySalaryStatsEnrichment.ts) never stamp salaryPeriod on their already-monthly blended
  // figures, so defaulting to 'annual' here silently divided this job's own gauge position by 12
  // (e.g. a €5,152-7,135 estimate rendered as €429-595, wrongly landing far below the benchmark).
  salaryPeriod = 'monthly',
  jobLocation = [],
  jobTitle,
  jobRoleLevel,
  brandColor,
}: EditorialSalaryGaugeProps): React.ReactElement | null {
  const intl = useIntl();
  const bandTint = brandColor ? hexToRgba(brandColor, 0.35) : null;
  const formatMessage = useFormatMessage();

  const benchmark = selectPrimarySalaryBenchmark({
    allSalaryStats,
    jobLocation,
    locationSalaryStats,
    companySalaryStats: salaryStats,
  });
  if (!benchmark) return null;

  const hasJobSalary = isPositiveNumber(jobSalaryMin) && isPositiveNumber(jobSalaryMax);
  const jobMinMonthly = hasJobSalary ? normalizeToMonthly(jobSalaryMin!, salaryPeriod) : null;
  const jobMaxMonthly = hasJobSalary ? normalizeToMonthly(jobSalaryMax!, salaryPeriod) : null;
  const jobMidpoint = jobMinMonthly != null && jobMaxMonthly != null ? (jobMinMonthly + jobMaxMonthly) / 2 : null;

  const analysis = buildSalaryContextAnalysis(
    jobMidpoint != null ? { min: jobMinMonthly!, max: jobMaxMonthly! } : null,
    { min: benchmark.stats.min, max: benchmark.stats.max, sampleSize: benchmark.stats.sampleSize },
  );

  const benchmarkLabel = (() => {
    switch (benchmark.kind) {
      case 'titleLocation':
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.titleLocation', defaultMessage: '{title} in {location}', description: 'Short benchmark label, editorial-rail salary gauge' },
          { title: jobTitle || formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role' }), location: benchmark.location },
        );
      case 'companyLocation':
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.companyLocation', defaultMessage: 'this company in {location}', description: 'Short benchmark label, editorial-rail salary gauge' },
          { location: benchmark.location },
        );
      case 'locationMarket':
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.locationMarket', defaultMessage: 'all roles in {location}', description: 'Short benchmark label, editorial-rail salary gauge' },
          { location: benchmark.location },
        );
      case 'titleRoleLevel':
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.titleRoleLevel', defaultMessage: '{roleLevel} level roles', description: 'Short benchmark label, editorial-rail salary gauge' },
          { roleLevel: jobRoleLevel || formatMessage({ id: 'salaryChart.thisRoleLevel', defaultMessage: 'this role level' }) },
        );
      case 'locationSalaryStats':
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.locationMarket', defaultMessage: 'all roles in {location}', description: 'Short benchmark label, editorial-rail salary gauge' },
          { location: benchmark.location },
        );
      case 'companySalaryStats':
        return formatMessage({
          id: 'jobDetailPageV2.salaryGauge.benchmark.companySalaryStats',
          defaultMessage: 'this company',
          description: 'Short benchmark label, editorial-rail salary gauge, company-wide fallback',
        });
      default:
        return formatMessage(
          { id: 'jobDetailPageV2.salaryGauge.benchmark.title', defaultMessage: '{title} roles', description: 'Short benchmark label, editorial-rail salary gauge' },
          { title: jobTitle || formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role' }) },
        );
    }
  })();

  const headline = (() => {
    if (analysis.placement === 'unknown' || analysis.differencePercent === null) {
      return formatMessage(
        { id: 'jobDetailPageV2.salaryGauge.headlineNoJobSalary', defaultMessage: 'Benchmarked against {benchmark}', description: 'Fallback salary-comparison headline when the listing has no numeric pay to compare, editorial-rail redesign' },
        { benchmark: benchmarkLabel },
      );
    }
    // A ±2% band reads as "in line with" rather than a false-precision "1% above"; same
    // threshold spirit as buildSalaryContextAnalysis's own 'similar' spread-comparison band.
    if (Math.abs(analysis.differencePercent) <= 2) {
      return formatMessage(
        { id: 'jobDetailPageV2.salaryGauge.headlineLevel', defaultMessage: 'In line with {benchmark}', description: 'One-line salary-comparison headline stat when the offer is within 2% of the benchmark median, editorial-rail redesign' },
        { benchmark: benchmarkLabel },
      );
    }
    return formatMessage(
      {
        id: 'jobDetailPageV2.salaryGauge.headline',
        defaultMessage: '{percent}% {direction, select, above {above} other {below}} {benchmark}',
        description: 'One-line salary-comparison headline stat, editorial-rail redesign',
      },
      {
        percent: Math.abs(analysis.differencePercent),
        direction: analysis.differencePercent > 0 ? 'above' : 'below',
        benchmark: benchmarkLabel,
      },
    );
  })();

  const axisMin = Math.min(benchmark.stats.min, jobMinMonthly ?? Infinity);
  const axisMax = Math.max(benchmark.stats.max, jobMaxMonthly ?? -Infinity);
  const axisSpan = axisMax - axisMin;
  if (!Number.isFinite(axisSpan) || axisSpan <= 0) return null;

  const toPercent = (value: number) => clampPercent(((value - axisMin) / axisSpan) * 100);
  const bandLeft = toPercent(benchmark.stats.percentile25.min);
  const bandRight = toPercent(benchmark.stats.percentile75.max);
  const medianMid = (benchmark.stats.median.min + benchmark.stats.median.max) / 2;
  const medianPct = toPercent(medianMid);
  const jobPct = jobMidpoint != null ? toPercent(jobMidpoint) : null;

  return (
    <div>
      <div className={`text-[12.5px] font-bold mb-2 ${brandColor ? '' : 'text-jc-accent'}`} style={brandColor ? { color: brandColor } : undefined}>
        {headline}
      </div>
      <div className="relative h-2 rounded-full bg-jc-border/70">
        <div
          className={`absolute inset-y-0 rounded-full ${bandTint ? '' : 'bg-jc-accent/35'}`}
          style={{ left: `${bandLeft}%`, width: `${Math.max(0, bandRight - bandLeft)}%`, background: bandTint ?? undefined }}
        />
        <div className="absolute top-[-3px] bottom-[-3px] w-0.5 bg-jc-ink-secondary" style={{ left: `${medianPct}%` }} />
        {jobPct != null ? (
          <div
            className={`absolute top-1/2 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-[#1F1509] shadow ${brandColor ? '' : 'bg-jc-accent'}`}
            style={{ left: `${jobPct}%`, transform: 'translate(-50%, -50%)', background: brandColor ?? undefined }}
            title={formatMessage({ id: 'jobDetailPageV2.salaryGauge.thisOffer', defaultMessage: 'This offer', description: 'Tooltip on the marker showing this job\'s own salary position on the gauge, editorial-rail redesign' })}
          />
        ) : null}
      </div>
      <div className="flex justify-between text-[10px] font-semibold text-jc-muted mt-1.5">
        <span>{formatCurrency(axisMin, intl.locale)}</span>
        <span>
          {formatCurrency(medianMid, intl.locale)}{' '}
          {formatMessage({ id: 'jobDetailPageV2.salaryGauge.median', defaultMessage: 'median', description: 'Label under the median tick on the salary gauge, editorial-rail redesign' })}
        </span>
        <span>{formatCurrency(axisMax, intl.locale)}</span>
      </div>
    </div>
  );
}
